import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Scan } from '../../types';

interface ProgressBadgeProps {
  firstScan: Scan;
  latestScan: Scan;
  metric?: 'score' | 'issues';
  size?: 'sm' | 'md' | 'lg';
}

const SIZE_CONFIG = {
  sm: {
    padding: 'px-2 py-1',
    text: 'text-caption',
    iconSize: 12,
  },
  md: {
    padding: 'px-3 py-1.5',
    text: 'text-body-sm',
    iconSize: 14,
  },
  lg: {
    padding: 'px-4 py-2',
    text: 'text-body',
    iconSize: 16,
  },
} as const;

export function ProgressBadge({ firstScan, latestScan, metric = 'issues', size = 'md' }: ProgressBadgeProps) {
  const sizeConfig = SIZE_CONFIG[size];

  // Calculate improvement percentage
  let percentChange: number;
  let isImprovement: boolean;

  if (metric === 'issues') {
    const firstIssues = (firstScan.results?.uniqueIssues ?? firstScan.results?.totalIssues)?.total ?? 0;
    const latestIssues = (latestScan.results?.uniqueIssues ?? latestScan.results?.totalIssues)?.total ?? 0;

    if (firstIssues === 0) {
      percentChange = latestIssues === 0 ? 0 : -100; // More issues = regression
    } else {
      // Positive = improvement (fewer issues)
      percentChange = ((firstIssues - latestIssues) / firstIssues) * 100;
    }
    isImprovement = percentChange > 0;
  } else {
    // Score metric
    const firstScore = firstScan.results?.overallScore ?? 0;
    const latestScore = latestScan.results?.overallScore ?? 0;

    if (firstScore === 0) {
      percentChange = latestScore > 0 ? 100 : 0;
    } else {
      // Positive = improvement (higher score)
      percentChange = ((latestScore - firstScore) / firstScore) * 100;
    }
    isImprovement = percentChange > 0;
  }

  const isNoChange = Math.abs(percentChange) < 0.5;

  // Determine styling based on improvement/regression
  let bgColor: string;
  let textColor: string;
  let Icon: typeof TrendingUp;

  if (isNoChange) {
    bgColor = 'bg-gray-100';
    textColor = 'text-gray-600';
    Icon = Minus;
  } else if (isImprovement) {
    bgColor = 'bg-green-100';
    textColor = 'text-green-700';
    Icon = TrendingUp;
  } else {
    bgColor = 'bg-red-100';
    textColor = 'text-red-700';
    Icon = TrendingDown;
  }

  // Format the label
  let label: string;
  if (isNoChange) {
    label = 'No change';
  } else if (isImprovement) {
    label = `${Math.abs(percentChange).toFixed(0)}% improvement`;
  } else {
    label = `${Math.abs(percentChange).toFixed(0)}% regression`;
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 ${sizeConfig.padding} rounded-full ${sizeConfig.text} font-medium ${bgColor} ${textColor}`}
      role="status"
      aria-label={label}
    >
      <Icon size={sizeConfig.iconSize} aria-hidden="true" />
      {label}
    </span>
  );
}
